import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { initializeAnalyticsClient } from '@confluence/analytics-web-client';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { initializeStorageManager } from '@confluence/storage-manager/entry-points/storageInstance';

interface PartialSessionData {
	cloudId: string;
	userId: string;
	environment: 'STAGING' | 'PRODUCTION';
}

const fetchGet = (resource: string) => {
	// eslint-disable-next-line no-restricted-syntax
	return fetch(resource, {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

const fetchPartialSessionDataFromAlternateSources: () => Promise<PartialSessionData> = () => {
	const fetchUserIdFromIdentity = async () => {
		const response = await fetchGet('/gateway/api/me');
		const responseData = await response.json();
		return responseData?.account_id;
	};

	const fetchTenantIdFromEdge = async () => {
		const response = await fetchGet('/_edge/tenant_info');
		const responseData = await response.json();
		return responseData?.cloudId;
	};

	const isStagingEnvironment = ['branch', 'staging', 'stg-fedm', 'dev'].includes(
		process.env.CLOUD_ENV,
	);

	return Promise.all([fetchUserIdFromIdentity(), fetchTenantIdFromEdge()]).then(
		([userId, cloudId]) => {
			return {
				cloudId,
				userId,
				environment: isStagingEnvironment ? 'STAGING' : 'PRODUCTION',
			};
		},
	);
};

// @TODO make session data call, and initialize more fully fleshed out Analytics Client (locale, eventFilterOptions)
// const fetchSessionDataFromGQL = () => {};

export const initialization = () => {
	return fetchPartialSessionDataFromAlternateSources()
		.then((data) => {
			const { cloudId, userId, environment } = data;

			const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
			const product = isEmbeddedConfluence ? 'embeddedConfluence' : 'confluence';

			/*
			 * Analytics Web Client initialization
			 *
			 * `initializeAnalyticsClient` is an asynchronous function. We start it but do not await it here. This allows the
			 * Error App UI, visible to the user, to be rendered ASAP
			 *
			 * The app relies on the consumers of the Analytics Client to await it when they use getAnalyticsWebClient.
			 */
			initializeAnalyticsClient({
				cloudId,
				userId,
				environment,
				product,
			});

			// Monitoring context initialization
			getMonitoringClient().updateContext({
				tenantId: cloudId,
				product,
			});

			// Storage manager initialization
			return initializeStorageManager({
				userId: userId ?? '',
			});
		})
		.catch(() => {
			// Unfortunately if we can't initialize the analytics client, we can't log the error
		});
};
